import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IDocuments } from 'interfaces/i-documents'
import { DocumentTypes } from 'constants/enums/documentTypes'
import DocumentViewer from 'components/DocumentViewer/DocumentViewer'
import ButtonSpinner from 'components/button-spinner/ButtonSpinner'
import { downloadBlob } from 'utils/documentHelpers'
import document_dark from 'img/document-icons/document-dark.svg'
//@ts-ignore
import { Document } from 'react-pdf'
import download_icon from 'img/credit-card-images/download_icon.svg'


const documentTypeLabels: Partial<Record<DocumentTypes, string>> = {
	[DocumentTypes.PrecontractCreditInformation]:
		'DocumentLabelPrecontractCreditInformation',
	[DocumentTypes.PromissoryNote]: 'DocumentLabelPromissoryNote',
}

interface DocumentBoxProps {
	blob: Blob | undefined
	document: IDocuments | undefined
	onStatusChange?: (allDone: boolean) => void
}

export const DocumentBox = ({
	blob,
	document,
	onStatusChange,
}: DocumentBoxProps) => {
	const { t } = useTranslation()
	const [isExpanded, setIsExpanded] = useState(false)
	const [hasBeenOpened, setHasBeenOpened] = useState(false)
	const [hasBeenDownloaded, setHasBeenDownloaded] = useState(false)
	const [docScale, setScale] = useState(1.0)
	const [loadingDownload, setLoadingDownload] = useState(false)
	const [numPages, setNumPages] = useState<number | null>(null)

	useEffect(() => {
		onStatusChange?.(hasBeenOpened && hasBeenDownloaded)
	}, [hasBeenOpened, hasBeenDownloaded, onStatusChange])

	const labelKey = document?.type
		? documentTypeLabels[document.type]
		: undefined
	const displayName = labelKey ? t(labelKey) : (document?.fileName ?? '')

	const fileType = blob?.type.split('/')[1]?.toUpperCase() ?? null
	const fileSizeKb = blob ? Math.round(blob.size / 1024) : null
	const metaParts = [
		fileType,
		numPages !== null ? `~${numPages} pages` : null,
		fileSizeKb !== null ? `${fileSizeKb} KB` : null,
	].filter(Boolean)

	const handleBoxClick = () => {
		if (!blob) return
		setIsExpanded((prev) => !prev)
		setHasBeenOpened(true)
	}

	const handleDownload = async (e: React.MouseEvent) => {
		e.stopPropagation()
		if (!blob || !document) return
		try {
			setLoadingDownload(true)
			downloadBlob(blob, document.fileName)
		} catch (err) {
			console.warn(err)
		} finally {
			setLoadingDownload(false)
			setHasBeenDownloaded(true)
		}
	}

	return (
		<div className="document-box">
			{blob && (
				<Document
					file={blob}
					onLoadSuccess={({ numPages: n }: { numPages: number }) =>
						setNumPages(n)
					}
					loading={null}
					error={null}
					noData={null}
				/>
			)}
			<div
				className="document-box-header"
				onClick={handleBoxClick}
				role="button"
				tabIndex={0}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') handleBoxClick()
				}}>
				<div className="document-box-left">
					<div className="document-box-info">
						<span className="document-box-filename">
							{displayName}
						</span>
						{metaParts.length > 0 && (
							<span className="document-box-meta">
								{metaParts.join(' · ')}
							</span>
						)}
					</div>
				</div>

				<div className="document-box-right">
					<button
						type="button"
						className={`document-box-open-btn ${hasBeenOpened ? 'opened completed-step' : 'not-opened'
							}`}
						onClick={(e) => {
							e.stopPropagation()
							handleBoxClick()
						}}
						disabled={!blob}>
						{hasBeenOpened ? t('Opened') : t('NotOpened')}
					</button>

					<button
						type="button"
						className="document-box-download-icon"
						onClick={handleDownload}
						disabled={loadingDownload || !blob}
						aria-label={t('DownloadPdf')}
						title={t('DownloadPdf')}>
						{loadingDownload ? (
							<ButtonSpinner />
						) : (
							<img
								src={download_icon}
								alt=""
								className="document-box-download-icon-image"
							/>
						)}
					</button>
				</div>
			</div>
			{isExpanded && blob && (
				<div className="document-box-viewer">
					<div id="document-viewer-container">
						<DocumentViewer
							blob={blob}
							docScale={docScale}
							setScale={setScale}
						/>
					</div>
				</div>
			)}
		</div>
	)
}